<script setup>
import $ from "jquery";
import { RouterView, useRouter } from 'vue-router';
import { onMounted, watch } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useCartorioStore } from '@/stores/cartorio';
import { useToast } from 'vue-toastification';
import helpers from '@/helpers';
import { usePermissao } from '@/composables/usePermissao';

const auth = useAuthStore();
const cartorioStore = useCartorioStore();
const router = useRouter();
const toast = useToast();
const { checarPermissaoOuRedirecionar } = usePermissao();

onMounted(async () => {

  // Carregar cartório do storage se existir
  cartorioStore.carregarCartorioDoStorage();
  await carregarHeaderFooter();
  
  // Configurar monitoramento do localStorage
  configurarMonitoramentoLocalStorage();
});

// Função para troca de cartório
const trocarCartorio = async () => {
  try {
    
    // Aguardar um pouco para garantir que o localStorage foi atualizado (reduzido de 1000ms para 300ms)
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Obtém e valida o novo cartório
    const novoCartorioData = localStorage.getItem('cartorio');
    
    if (!novoCartorioData) {
      throw new Error('Nenhum cartório selecionado');
    }

    const novoCartorio = JSON.parse(novoCartorioData);
    
    // Usar validação do store
    if (!cartorioStore.validarDadosCartorio(novoCartorio)) {
      throw new Error('Dados do cartório inválidos');
    }
    
    // Verificar se o cartório realmente mudou
    const cartorioAtual = cartorioStore.cartorioAtual;
    if (cartorioAtual && cartorioAtual.cns === novoCartorio.cns) {
      return;
    }
    
    // Verificar permissão baseada na função
    const funcao = novoCartorio.funcao;
    if (!checarPermissaoOuRedirecionar(funcao)) {
      return;
    }
    
    // Usar o store para trocar cartório
    await cartorioStore.trocarCartorio(novoCartorio);

    // Recarrega o header com os novos dados (apenas se necessário)
    await carregarHeaderFooter();

    // Se estiver na página inicial, aguardar a atualização dos dados
    const currentRoute = router.currentRoute.value;
    if (currentRoute.path === '/') {
      await aguardarAtualizacaoDados();
    }

  } catch (error) {
    console.error('Erro ao trocar cartório:', error);
    toast.error('Erro ao trocar cartório. Por favor, tente novamente.');
  }
};

const carregarHeaderFooter = async () => {
  const suporte = {
    telefone: '(11) 5555-9252',
    email: 'suporte.crc@registrocivil.org.br',
  };
  let script = document.createElement('script');
  script.setAttribute('type', 'module');
  script.innerHTML = `
        import {instalarHeaderFooter} from '${
          import.meta.env.VITE_URL_BASE_HEADER_FOOTER
        }/header-footer.min.js';
          instalarHeaderFooter({
              usuario: {
                  nome: '${auth.user?.name || ''}',
                  cpf: '${helpers.formatarCPF(auth.user?.cpf) || ''}',
                  foto: '${auth.user?.foto_de_perfil_url || ''}',
                  grupos: ${JSON.stringify(auth.user?.groups || [])}
              },
              urlLogin: '${auth.urlLoginScreen()}',
              urlLogout: '${auth.urlLogout()}',
              token: '${auth.getToken()}',
              cartorio: '${cartorioStore.nomeCartorio}',
              exibirPerguntasFrequentes: ${true},
              exibirFaleConoscoPeloChat: ${true},
              suporte: '${JSON.stringify(suporte)}',
              exibirAcessoRemoto: ${true},
              exibirWhatsApp: ${true},
          });
      `;
  document.body.appendChild(script);

  // Configurar observer para detectar mudanças no header
  configurarObserverHeader();
};

// Função para configurar observer do header
const configurarObserverHeader = () => {
  // Observer para detectar quando o header é carregado
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Procurar pelo botão botaoSelecionarCartorio
            const botaoCartorio = node.querySelector('#botaoSelecionarCartorio') || 
                                 (node.id === 'botaoSelecionarCartorio' ? node : null);
            
            if (botaoCartorio) {
              configurarListenerBotaoCartorio(botaoCartorio);
            }
            
            // Procurar recursivamente nos filhos
            const botaoRecursivo = node.querySelector('#botaoSelecionarCartorio');
            if (botaoRecursivo) {
              configurarListenerBotaoCartorio(botaoRecursivo);
            }
          }
        });
      }
    });
  });

  // Observar mudanças no header
  const header = document.getElementById('header');
  if (header) {
    observer.observe(header, { 
      childList: true, 
      subtree: true 
    });
  }

  // Também verificar se o botão já existe
  setTimeout(() => {
    const botaoExistente = document.getElementById('botaoSelecionarCartorio');
    if (botaoExistente) {
      configurarListenerBotaoCartorio(botaoExistente);
    }
  }, 1000);
};

// Função para configurar listener no botão
const configurarListenerBotaoCartorio = (botao) => {
  // Remover listeners existentes para evitar duplicação
  botao.removeEventListener('click', handleBotaoCartorioClick);
  
  // Adicionar novo listener
  botao.addEventListener('click', handleBotaoCartorioClick);
};

// Handler para o clique no botão
const handleBotaoCartorioClick = async (event) => {
  event.preventDefault();
  event.stopPropagation();
  
  try {
    // Mostrar loading
    const screenLoading = document.getElementById('screenLoading');
    if (screenLoading) {
      screenLoading.style.display = 'block';
    }
    
    // Aguardar um pouco para o header processar a seleção (reduzido de 500ms para 200ms)
    await new Promise(resolve => setTimeout(resolve, 200));
    
    // Iniciar fluxo de troca de cartório (já inclui aguardarAtualizacaoDados)
    await trocarCartorio();
    
    // Esconder loading
    if (screenLoading) {
      screenLoading.style.display = 'none';
    }
  } catch (error) {
    console.error('Erro ao processar clique do botão cartório:', error);
    // Esconder loading em caso de erro
    const screenLoading = document.getElementById('screenLoading');
    if (screenLoading) {
      screenLoading.style.display = 'none';
    }
  }
};

// Função para aguardar a atualização dos dados
const aguardarAtualizacaoDados = async () => {
  return new Promise((resolve) => {
    // Verificar se estamos na página inicial
    const currentRoute = router.currentRoute.value;
    if (currentRoute.path !== '/') {
      // Se não estiver na página inicial, não precisa aguardar
      resolve();
      return;
    }

    // Criar um listener temporário para aguardar a atualização dos dados
    const handleDadosAtualizados = () => {
      window.removeEventListener('dados-conta-atualizados', handleDadosAtualizados);
      resolve();
    };

    window.addEventListener('dados-conta-atualizados', handleDadosAtualizados);

    // Timeout de segurança (reduzido de 10 segundos para 5 segundos)
    setTimeout(() => {
      window.removeEventListener('dados-conta-atualizados', handleDadosAtualizados);
      resolve();
    }, 5000);
  });
};

// Modificar o watch para evitar loops
watch(
  () => auth.user,
  async (newValue, oldValue) => {
    if (newValue && (!oldValue || newValue.id !== oldValue.id)) {
      await carregarHeaderFooter();
    }
  },
  { deep: true }
);

// Função para monitorar mudanças no localStorage
const configurarMonitoramentoLocalStorage = () => {
  // Função para verificar mudanças no localStorage
  const verificarMudancaLocalStorage = () => {
    const cartorioData = localStorage.getItem('cartorio');
    if (cartorioData) {
      try {
        const cartorio = JSON.parse(cartorioData);
        const cartorioAtual = cartorioStore.cartorioAtual;
        
        // Verificar se houve mudança real
        if (!cartorioAtual || cartorioAtual.cns !== cartorio.cns) {
          trocarCartorio();
        }
      } catch (error) {
        console.error('Erro ao verificar localStorage:', error);
      }
    }
  };

  // Verificar a cada 1000ms se houve mudança no localStorage (otimizado de 500ms para 1000ms)
  setInterval(verificarMudancaLocalStorage, 1000);
  
  // Também configurar listener de storage (para mudanças de outras abas)
  window.addEventListener('storage', async (e) => {
    if (e.key === 'cartorio') {
      $('#screenLoading').show();
      await trocarCartorio();
      $('#screenLoading').hide();
    }
  });
};
</script>

<template>
  
  <div id="appContainer">
    
    <div id="header"></div>
    <div id="body" class="">
      <div class="container-fluid">
        <RouterView />
      </div>  
    </div>
    <div id="footer"></div>

  </div>
</template>

<style scoped lang="scss">

#appContainer {
  height: 100%;
  display: flex;
  flex-direction: column;
}

#header,
#body,
#body > div,
#footer {
  flex: 1;
}

#header,
#footer {
  display: contents;
}

#body {
  display: flex;

  > div {
    min-height: 640px;
    display: flex;
    justify-content: center;
    width: 100%;
  }
}
</style>
